<!--
Fork addition: click-to-place menu. A genuine left click on empty
board opens this at the click point: a focused search box over a
grouped factory list (Name: / Input recipe: / Output recipe: /
Storage: — the same structure as the left panel's live search). The
most recently placed factory is preselected, so Enter places it again
at the clicked point; clicking an entry places it; Esc or clicking
elsewhere closes without placing.
-->
<script setup lang="ts">
import type {GameItem} from '#types/game-data';
import {injectGameData} from '@/scripts/data';
import {findStorageMatches} from '@/scripts/storage';
import {useLastPlacedFactory} from '@/composables';
import {mdiClose} from '@mdi/js';
import {computed, nextTick, ref, unref} from 'vue';
import type {ReadonlyPointType} from '@/scripts/geometry';

const gameData = injectGameData();
const {lastPlacedName} = useLastPlacedFactory();

const emit = defineEmits<{
    (e: 'place', factory: GameItem): void;
}>();

type BadgeKind = 'in' | 'out' | 'both';
interface PlaceGroup {
    title: string;
    items: GameItem[];
    badges?: Map<string, {glyph: string; title: string; kind: BadgeKind}>;
}

const active = ref<boolean>(false);
const search = ref<string>('');
const menuPosition = ref<{x: number; y: number}>({x: 0, y: 0});
const highlightedName = ref<string | undefined>();
const listElement = ref<HTMLElement | null>(null);

const menuStyle = computed(() => {
    const pos = unref(menuPosition);
    return {
        left: `${Math.max(8, Math.min(pos.x, window.innerWidth - 340))}px`,
        top: `${Math.max(8, Math.min(pos.y, window.innerHeight - 500))}px`,
    };
});

//does this factory consume / produce the product? mirrors the
//predicate in Filter.buildFilter (exact product + abstract class
//equivalents), same as the left panel's live search
function ioRelation(item: GameItem, product: GameItem): BadgeKind | undefined {
    const recipeDictionary = item.recipeDictionary;
    if(!recipeDictionary)
        return undefined;
    const abstractType = product.isAbstractClassItem ? product.type : undefined;
    const relativeAbstract = product.type ? gameData.gameAbstractItems.get(product.type) : undefined;
    const consumes = recipeDictionary.recipesByInputMap.has(product.name)
        || ((abstractType !== undefined) && recipeDictionary.hasInputTypes.has(abstractType))
        || (!!relativeAbstract && recipeDictionary.recipesByInputMap.has(relativeAbstract.name));
    const produces = recipeDictionary.recipesByOutputMap.has(product.name)
        || ((abstractType !== undefined) && recipeDictionary.hasOutputTypes.has(abstractType))
        || (!!relativeAbstract && recipeDictionary.recipesByOutputMap.has(relativeAbstract.name));
    if(consumes && produces)
        return 'both';
    if(consumes)
        return 'in';
    if(produces)
        return 'out';
    return undefined;
}

const groups = computed<PlaceGroup[]>(() => {
    const query = unref(search).trim().toLowerCase();
    const factories = gameData.gameFactoriesArray;
    if(!query) {
        //unfiltered: every factory A–Z; the last placed one is the
        //preselected highlight (set on activate)
        return [{title: '', items: [...factories].sort((a, b) => a.label.localeCompare(b.label))}];
    }

    const nameMatches = factories
        .map((item) => {
            const label = item.lowerLabel;
            const name = item.name.toLowerCase();
            let rank: number;
            if(label.startsWith(query))
                rank = 0;
            else if(name.startsWith(query))
                rank = 1;
            else if(label.indexOf(query) > -1)
                rank = 2;
            else if(name.indexOf(query) > -1)
                rank = 3;
            else
                return undefined;
            return {item, rank};
        })
        .filter((entry): entry is {item: GameItem; rank: number} => !!entry)
        .sort((a, b) => (a.rank - b.rank) || a.item.label.localeCompare(b.item.label))
        .map(({item}) => item);
    const inNameMatches = new Set(nameMatches.map((item) => item.name));

    //best product match: label prefix beats substring, shortest label
    //wins ties, then A–Z (same resolution as the left panel)
    let product: GameItem | undefined = undefined;
    let productRank = Number.POSITIVE_INFINITY;
    for(const item of gameData.gameItemsArray) {
        if(item.recipeDictionary)
            continue;
        const label = item.lowerLabel;
        let rank: number;
        if(label.startsWith(query))
            rank = 0;
        else if(label.indexOf(query) > -1)
            rank = 1;
        else
            continue;
        if((rank < productRank)
            || ((rank === productRank) && product && ((label.length < product.lowerLabel.length)
                || ((label.length === product.lowerLabel.length) && (label < product.lowerLabel))))
        ) {
            product = item;
            productRank = rank;
        }
    }

    const result: PlaceGroup[] = [];
    if(nameMatches.length)
        result.push({title: 'Name:', items: nameMatches});
    if(product) {
        const consumers: GameItem[] = [];
        const producers: GameItem[] = [];
        const inBadges = new Map<string, {glyph: string; title: string; kind: BadgeKind}>();
        const outBadges = new Map<string, {glyph: string; title: string; kind: BadgeKind}>();
        for(const item of factories) {
            if(inNameMatches.has(item.name))
                continue;
            const relation = ioRelation(item, product);
            if((relation === 'in') || (relation === 'both')) {
                consumers.push(item);
                inBadges.set(item.name, {glyph: '↓', title: `Consumes ${product.label}`, kind: 'in'});
            }
            if((relation === 'out') || (relation === 'both')) {
                producers.push(item);
                outBadges.set(item.name, {glyph: '↑', title: `Produces ${product.label}`, kind: 'out'});
            }
        }
        consumers.sort((a, b) => a.label.localeCompare(b.label));
        producers.sort((a, b) => a.label.localeCompare(b.label));
        if(consumers.length)
            result.push({title: 'Input recipe:', items: consumers, badges: inBadges});
        if(producers.length)
            result.push({title: 'Output recipe:', items: producers, badges: outBadges});
        const storageMatches = findStorageMatches(gameData, product, 'any')
            .filter((match) => !inNameMatches.has(match.factory.name));
        if(storageMatches.length) {
            const badges = new Map<string, {glyph: string; title: string; kind: BadgeKind}>();
            const productLabel = product.label;
            const items = storageMatches.map((match) => {
                const kind: BadgeKind = (match.acceptsInput && match.producesOutput)
                    ? 'both'
                    : (match.acceptsInput ? 'in' : 'out');
                badges.set(match.factory.name, {
                    glyph: (kind === 'both') ? '⇅' : (kind === 'in') ? '↓' : '↑',
                    title: `Stores ${productLabel}`,
                    kind,
                });
                return match.factory;
            });
            items.sort((a, b) => a.label.localeCompare(b.label));
            result.push({title: 'Storage:', items, badges});
        }
    }
    return result;
});

const flatItems = computed<GameItem[]>(() => unref(groups).flatMap((group) => group.items));
const highlightedItem = computed<GameItem | undefined>(() => {
    const name = unref(highlightedName);
    return unref(flatItems).find((item) => item.name === name) || unref(flatItems)[0];
});

function activate(screenPosition: ReadonlyPointType) {
    menuPosition.value = {x: screenPosition.x, y: screenPosition.y};
    search.value = '';
    //the most recently placed factory starts preselected, so Enter
    //immediately places it again at the new point
    highlightedName.value = unref(lastPlacedName);
    active.value = true;
    nextTick(() => {
        const input = document.querySelector('.place-menu input') as HTMLInputElement | null;
        input?.focus();
        scrollHighlightedIntoView();
    });
}

function closeMenu() {
    active.value = false;
}

function place(factory: GameItem) {
    active.value = false;
    emit('place', factory);
}

function scrollHighlightedIntoView() {
    const list = unref(listElement);
    const name = unref(highlightedName);
    if(!list || !name)
        return;
    const row = list.querySelector(`[data-place-name="${CSS.escape(name)}"]`);
    row?.scrollIntoView({block: 'nearest'});
}

function moveHighlight(offset: number) {
    const items = unref(flatItems);
    if(!items.length)
        return;
    const current = unref(highlightedItem);
    const index = current ? items.indexOf(current) : -1;
    const next = items[Math.max(0, Math.min(items.length - 1, index + offset))];
    if(next) {
        highlightedName.value = next.name;
        nextTick(scrollHighlightedIntoView);
    }
}

function onSearchKeydown(event: KeyboardEvent) {
    if(event.key === 'Enter') {
        const item = unref(highlightedItem);
        if(item)
            place(item);
    } else if(event.key === 'ArrowDown') {
        event.preventDefault();
        moveHighlight(1);
    } else if(event.key === 'ArrowUp') {
        event.preventDefault();
        moveHighlight(-1);
    } else if(event.key === 'Escape') {
        closeMenu();
    }
}

function onSearchInput() {
    //typing re-ranks the list; keep the highlight on the first entry
    highlightedName.value = unref(flatItems)[0]?.name;
}

defineExpose({
    activate,
});
</script>

<template>
    <Teleport to="body">
        <div v-if="active" class="place-menu-backdrop" @click="closeMenu" />
        <div v-if="active" class="place-menu" :style="menuStyle">
            <v-card elevation="8" class="menu-card">
                <v-card-title class="menu-header">
                    <span>Place factory</span>
                    <v-btn
                        size="small"
                        variant="text"
                        :icon="mdiClose"
                        @click="closeMenu"
                    />
                </v-card-title>
                <v-divider />
                <div class="menu-search">
                    <v-text-field
                        v-model="search"
                        density="compact"
                        placeholder="Search factories & products…"
                        hide-details
                        clearable
                        @keydown="onSearchKeydown"
                        @input="onSearchInput"
                    />
                </div>
                <div ref="listElement" class="menu-list">
                    <template v-for="(group, groupIndex) in groups" :key="groupIndex">
                        <div v-if="group.title" class="group-heading">
                            {{ group.title }}
                        </div>
                        <div
                            v-for="item in group.items"
                            :key="item.name"
                            class="place-row"
                            :class="{'place-row-highlighted': item.name === highlightedItem?.name}"
                            :data-place-name="item.name"
                            @click="place(item)"
                            @mouseenter="highlightedName = item.name"
                        >
                            <icon-component :image="item.image" :data-tooltip="item.label" />
                            <span class="place-row-name">{{ item.label }}</span>
                            <span
                                v-if="group.badges?.get(item.name)"
                                class="io-badge"
                                :class="`io-badge-${group.badges.get(item.name)?.kind}`"
                                :title="group.badges.get(item.name)?.title"
                            >{{ group.badges.get(item.name)?.glyph }}</span>
                            <span
                                v-if="item.name === lastPlacedName"
                                class="last-placed-tag"
                                title="Most recently placed — Enter places it again"
                            >last</span>
                        </div>
                    </template>
                    <div v-if="!flatItems.length" class="empty-note">
                        No factories match
                    </div>
                </div>
            </v-card>
        </div>
    </Teleport>
</template>

<style scoped>
.place-menu-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9998;
    background: transparent;
}
.place-menu {
    position: fixed;
    z-index: 9999;
}
.menu-card {
    width: 320px;
    max-height: 480px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
.menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    font-size: 0.875rem;
}
.menu-search {
    padding: 8px 12px 4px;
}
.menu-list {
    overflow-y: auto;
    flex: 1;
    padding: 4px 6px 8px;
}
.group-heading {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    opacity: 0.65;
    padding: 8px 6px 2px;
}
.place-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 6px;
    border-radius: 4px;
    cursor: pointer;
}
.place-row:hover {
    background: rgba(128, 128, 128, 0.15);
}
.place-row-highlighted {
    background: rgba(128, 128, 128, 0.22);
    box-shadow: inset 0 0 0 1px rgba(128, 128, 128, 0.5);
}
.place-row-name {
    font-size: 0.85rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.last-placed-tag {
    margin-left: auto;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    opacity: 0.6;
}
.io-badge {
    margin-left: auto;
    font-size: 0.8rem;
    font-weight: 700;
}
.io-badge-in {
    color: #00897b;
}
.io-badge-out {
    color: #ef6c00;
}
.io-badge-both {
    color: #7b1fa2;
}
.empty-note {
    padding: 12px 6px;
    font-size: 0.85rem;
    opacity: 0.65;
}
</style>
